import {
  Alert,
  AlertDescription,
  ConfirmDialog,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import { ProfileTemplateDetailOverview } from '@/core/components/profile-templates/profile-template-detail-overview';
import { TemplateDetailBasicsSection } from '@/core/components/profile-templates/template-detail-basics-section';
import { TemplateDetailHeaderActions } from '@/core/components/profile-templates/template-detail-header-actions';
import { TemplateDetailHistoryTab } from '@/core/components/profile-templates/template-detail-history-tab';
import { TemplateDetailLeadFormSection } from '@/core/components/profile-templates/template-detail-lead-form-section';
import { TemplateDetailModulesTab } from '@/core/components/profile-templates/template-detail-modules-tab';
import {
  TemplateDetailPreviewColumn,
  TemplateDetailPreviewDialog,
} from '@/core/components/profile-templates/template-detail-preview-pane';
import { TemplateDetailSectionCard } from '@/core/components/profile-templates/template-detail-section-card';
import { TemplateDetailSettingsSection } from '@/core/components/profile-templates/template-detail-settings-section';
import { useTemplateDetailEditor } from '@/core/components/profile-templates/use-template-detail-editor';
import { AutosaveStatusCue } from '@/core/components/ui/autosave-status-cue';
import { AnimatedUnderlineTabs } from '@/core/components/ui/tabs-29';
import { shellPaths } from '@/core/router/app-paths';
import { AppIonPage } from '@/core/components/app-ion-page';

function TemplateDetailBreadcrumb({
  domainLabel,
  templatesLabel,
}: {
  domainLabel: string;
  templatesLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {templatesLabel}
    </>
  );
}

export function ProfileTemplateDetailPage() {
  const {
    dict,
    pt,
    pe,
    workspace,
    templateId,
    loading,
    canManage,
    canEditTemplate,
    workspaceHasTeam,
    autosaveStatus,
    saveError,
    name,
    savedName,
    description,
    policies,
    modules,
    templateTeamIds,
    publishStatus,
    publishing,
    discardingChanges,
    liveRevision,
    revisionHistoryKey,
    activeTab,
    setActiveTab,
    showPreview,
    setShowPreview,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    isLeavePromptOpen,
    setLeavePromptOpen,
    confirmLeave,
    errors,
    handlePolicyChange,
    policyLabels,
    watchedForm,
    watchedJobTitle,
    watchedCompany,
    watchedLocation,
    watchedLanguage,
    watchedVisibility,
    isValueDisabled,
    sharedMemberValueProps,
    handleModulesChange,
    handleModuleEntryPolicyChange,
    handleBack,
    handleDelete,
    overviewDict,
    revisionHistoryDict,
    handleViewRevisionHistory,
    handleRevisionRestored,
    handleNameChange,
    handleDescriptionChange,
    handleFieldChange,
    handleTeamIdsChange,
    handlePublish,
    handleDiscardChanges,
    publishStatusLabel,
    previewProfileData,
  } = useTemplateDetailEditor();

  const breadcrumb = (
    <TemplateDetailBreadcrumb
      domainLabel={dict.nav.navDomainPeople}
      templatesLabel={dict.nav.profileTemplates}
    />
  );

  const headerActions = canManage ? (
    <TemplateDetailHeaderActions
      moreOptionsLabel={dict.nav.moreOptions}
      previewLabel={pt.detailOverview.preview}
      deleteLabel={pt.delete}
      deleteAriaLabel={dict.nav.delete}
      canEditTemplate={canEditTemplate}
      onPreview={() => setShowPreview(true)}
      onDelete={() => setDeleteConfirmOpen(true)}
    />
  ) : undefined;

  if (!workspace) {
    return (
      <AppIonPage>
        <AppPageContent>
          <AppPageLayout>
            <PageHeader
              title={pt.title}
              breadcrumb={breadcrumb}
              description={pt.selectWorkspace}
            />
          </AppPageLayout>
        </AppPageContent>
      </AppIonPage>
    );
  }

  if (loading) {
    return (
      <AppIonPage>
        <AppPageContent>
          <AppPageLayout className="min-w-0 max-w-full overflow-x-hidden md:pb-4">
            <PageHeader
              title={pt.title}
              breadcrumb={breadcrumb}
              backHref={shellPaths.moreProfileTemplates}
              onBack={handleBack}
            />
            <div className="flex min-w-0 flex-col gap-4">
              <TemplateDetailSectionCard contentClassName="gap-4">
                <Skeleton className="h-8 w-40" />
                <Skeleton className="h-4 w-full max-w-md" />
              </TemplateDetailSectionCard>
              <TemplateDetailSectionCard contentClassName="gap-4">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-24 w-full" />
              </TemplateDetailSectionCard>
            </div>
          </AppPageLayout>
        </AppPageContent>
      </AppIonPage>
    );
  }

  const tabs = [
    {
      name: pe.basicInformation.title,
      value: 'basic',
      content: (
        <TemplateDetailBasicsSection
          dict={{
            ...pe.basicInformation,
            description: pt.sectionDescriptions.basics,
            showProfileImageDescription:
              pt.sectionDescriptions.showProfileImage,
          }}
          fieldLabels={{
            jobTitle: pt.fields.jobTitle,
            company: pt.fields.company,
            location: pt.fields.location,
            showProfileImage: pt.fields.showProfileImage,
          }}
          policies={policies}
          onPolicyChange={handlePolicyChange}
          canEditTemplate={canEditTemplate}
          policyLabels={policyLabels}
          memberValueProps={sharedMemberValueProps}
          watchedJobTitle={watchedJobTitle}
          watchedCompany={watchedCompany}
          watchedLocation={watchedLocation}
          watchedForm={watchedForm}
          disabled={isValueDisabled()}
          onFieldChange={handleFieldChange}
        />
      ),
    },
    {
      name: pe.leadForm.title,
      value: 'leadForm',
      content: (
        <TemplateDetailLeadFormSection
          leadFormDict={pe.leadForm}
          leadsWorkspaceDict={pe.leadsWorkspace}
          leadsAutoShareFieldLabel={pt.fields.leadsAutoShareWithWorkspace}
          policies={policies}
          onPolicyChange={handlePolicyChange}
          canEditTemplate={canEditTemplate}
          policyLabels={policyLabels}
          memberValueProps={sharedMemberValueProps}
          watchedForm={watchedForm}
          disabled={isValueDisabled()}
          showLeadsWorkspaceShare={!workspace.leadsAlwaysSharedWithWorkspace}
          onFieldChange={handleFieldChange}
        />
      ),
    },
    {
      name: 'Module',
      value: 'modules',
      content: (
        <TemplateDetailModulesTab
          modules={modules}
          onModulesChange={handleModulesChange}
          isWorkspaceProfile={Boolean(workspace) && workspaceHasTeam}
          templateTeamIds={templateTeamIds}
          canEditTemplate={canEditTemplate}
          disabled={isValueDisabled()}
          policyLabels={policyLabels}
          onModuleEntryPolicyChange={handleModuleEntryPolicyChange}
          memberValueProps={sharedMemberValueProps}
        />
      ),
    },
    {
      name: pe.settings.title,
      value: 'settings',
      content: (
        <TemplateDetailSettingsSection
          dict={{
            ...pe.settings,
            description: pt.sectionDescriptions.settings,
            showUp2promoBrandingDescription:
              pt.sectionDescriptions.showUp2promoBranding,
            showUp2promoBrandingUpgradeDescription:
              pt.sectionDescriptions.brandingUpgrade,
            accentColorDescription: pt.sectionDescriptions.accentColor,
          }}
          fieldLabels={{
            language: pt.fields.language,
            visibility: pt.fields.visibility,
            showUp2promoBranding: pt.fields.showUp2promoBranding,
            layout: pt.fields.layout,
          }}
          policies={policies}
          onPolicyChange={handlePolicyChange}
          canEditTemplate={canEditTemplate}
          policyLabels={policyLabels}
          memberValueProps={sharedMemberValueProps}
          accentCtaLabel={dict.publicProfile.saveContact}
          watchedForm={watchedForm}
          watchedLanguage={watchedLanguage}
          watchedVisibility={watchedVisibility}
          languageError={errors.language}
          disabled={isValueDisabled()}
          onFieldChange={handleFieldChange}
        />
      ),
    },
    {
      name: pt.revisionHistory.tabTitle,
      value: 'history',
      content:
        workspace.id && templateId ? (
          <TemplateDetailHistoryTab
            workspaceId={workspace.id}
            templateId={templateId}
            workspaceUsername={workspace.username ?? null}
            previewSampleName={pt.previewSampleName}
            previewSampleUsername={pt.previewSampleUsername}
            canManage={canManage}
            publishStatus={publishStatus}
            liveRevision={liveRevision}
            revisionHistoryKey={revisionHistoryKey}
            onRestored={handleRevisionRestored}
            dict={revisionHistoryDict}
          />
        ) : null,
    },
  ];

  return (
    <AppIonPage>
      <AppPageContent hideScrollbar>
        <AppPageLayout className="min-w-0 max-w-full overflow-x-hidden md:pb-4">
          <PageHeader
            title={name.trim() || savedName || pt.title}
            breadcrumb={breadcrumb}
            backHref={shellPaths.moreProfileTemplates}
            onBack={handleBack}
            actions={headerActions}
          />
          <div className="relative flex min-w-0 flex-1 flex-col gap-4">
            {!canManage ? (
              <Alert className="min-w-0 w-full">
                <AlertDescription>{pt.readOnlyHint}</AlertDescription>
              </Alert>
            ) : null}

            {/* Nothing autosaves while the name is empty (DEV-538). */}
            {name.trim() ? (
              <AutosaveStatusCue
                status={autosaveStatus}
                className="pointer-events-none absolute left-0 top-0 z-10 -translate-y-full"
              />
            ) : null}

            {saveError ? (
              <Alert variant="destructive" className="min-w-0 w-full">
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            ) : null}

            <ProfileTemplateDetailOverview
              name={name}
              description={description}
              onNameChange={handleNameChange}
              onDescriptionChange={handleDescriptionChange}
              canManage={canEditTemplate}
              publishStatus={publishStatus}
              publishStatusLabel={publishStatusLabel}
              publishing={publishing}
              discardingChanges={discardingChanges}
              onPublish={() => void handlePublish()}
              onDiscardChanges={() => void handleDiscardChanges()}
              workspaceId={workspace.id}
              templateTeamIds={templateTeamIds}
              onTeamIdsChange={handleTeamIdsChange}
              dict={overviewDict}
              liveRevision={liveRevision}
              onViewRevisionHistory={handleViewRevisionHistory}
            />

            <AnimatedUnderlineTabs
              tabs={tabs}
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
              rightColumn={
                <TemplateDetailPreviewColumn
                  workspaceId={workspace.id}
                  workspaceUsername={workspace.username ?? null}
                  previewSampleUsername={pt.previewSampleUsername}
                  profileData={previewProfileData}
                />
              }
            />
          </div>
        </AppPageLayout>
      </AppPageContent>

      <ConfirmDialog
        open={isLeavePromptOpen}
        onOpenChange={setLeavePromptOpen}
        title={pt.leaveDraftConfirmTitle}
        description={pt.leaveDraftConfirmDescription}
        confirmLabel={pt.leaveDraftConfirmLeave}
        cancelLabel={pt.leaveDraftConfirmStay}
        onConfirm={confirmLeave}
        closeBehavior="always"
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={pt.deleteConfirmTitle}
        description={pt.deleteConfirmDescription.replace(
          '{name}',
          name.trim() || savedName || pt.title,
        )}
        confirmLabel={pt.deleteConfirm}
        cancelLabel={pt.deleteConfirmCancel}
        destructive
        onConfirm={handleDelete}
      />

      <TemplateDetailPreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        workspaceId={workspace.id}
        workspaceUsername={workspace.username ?? null}
        previewSampleUsername={pt.previewSampleUsername}
        profileData={previewProfileData}
      />
    </AppIonPage>
  );
}
